import {
  CreateClientPayload,
  SearchClientsPayload,
  SearchEarningPayload,
  SearchRedemptionPayload,
} from "@/domain/client.interface";
import { ClientsService } from "@/services/clients.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const useClients = ({
  sortField,
  sortOrder,
  pageNumber,
  pageSize,
  search,
  status,
  programId,
  orgUnitId,
  userId,
}: SearchClientsPayload) => {
  const {
    data: clients,
    isLoading: isLoadingClients,
    isFetching: isFetchingClients,
    refetch: refetchClients,
  } = useQuery({
    queryKey: [
      "pret-clients",
      sortField,
      sortOrder,
      pageNumber,
      pageSize,
      search,
      status,
      programId,
      orgUnitId,
      userId,
    ],
    queryFn: () =>
      ClientsService.findAll({
        sortField,
        sortOrder,
        pageNumber,
        pageSize,
        search,
        status,
        programId,
        orgUnitId,
        userId,
      }),
    enabled: programId !== "",
  });

  return {
    clients,
    isLoadingClients,
    isFetchingClients,
    refetchClients,
  };
};

export const useCreateClient = () => {
  return useMutation({
    mutationFn: (data: CreateClientPayload) => ClientsService.create(data),
  });
};

export const useUpdateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      participantId: string;
      enrollmentId: string;
      editClient: CreateClientPayload;
    }) =>
      ClientsService.update({
        participantId: data.participantId,
        enrollmentId: data.enrollmentId,
        data: data.editClient,
      }),
    /**
     * PRET-1917 — a client update can MOVE the first appointment, so the appointments card has to
     * be refetched, not just the participant.
     *
     * Reported after testing: the new date only appeared after reloading the browser tab. The
     * profile page called `refetchParticipant()` on success, but the card reads a separate query
     * (`participant-appointments`, in useAppointments) with `staleTime: 5 minutes` — so it went on
     * serving the pre-correction date from cache for up to five minutes, and a reload was the only
     * way to see the change.
     *
     * Invalidated here rather than in the modal because it is the UPDATE that moves the
     * appointment: every caller of this mutation has the same exposure, and one that forgets would
     * reintroduce a stale card that looks like the save silently failed.
     *
     * The key is a prefix — react-query matches from the start — so it covers every page, page
     * size, sort and filter the card may be showing.
     */
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["participant-appointments", variables.participantId],
      });
    },
  });
};

export const useDismissedClient = () => {
  return useMutation({
    mutationFn: (data: {
      participantId: string;
      enrollmentId: string;
      updatedBy: string;
    }) => ClientsService.dismissed(data),
  });
};

export const useGetEarnings = ({
  sortField,
  sortOrder,
  pageNumber,
  pageSize,
  search,
  status,
  programId,
  enrollmentId,
}: SearchEarningPayload) => {
  const {
    data: earningsResponse,
    isLoading: isLoadingClients,
    isFetching: isFetchingClients,
    refetch: refetchEarnings,
  } = useQuery({
    queryKey: ["pret-earnings"],
    queryFn: () =>
      ClientsService.findAllEarnings({
        sortField,
        sortOrder,
        pageNumber,
        pageSize,
        search,
        status,
        programId,
        enrollmentId,
      }),
    enabled: !!enrollmentId,
  });

  return {
    earningsResponse,
    isLoadingClients,
    isFetchingClients,
    refetchEarnings,
  };
};

export const useGetRedemptions = ({
  accountId,
  enrollmentId,
  sortField,
  sortOrder,
  pageNumber,
  pageSize,
  mechanism,
}: SearchRedemptionPayload) => {
  const {
    data: redemptionsResponse,
    isLoading: isLoadingRedemptions,
    isFetching: isFetchingRedemptions,
    refetch: refetchRedemptions,
  } = useQuery({
    queryKey: ["pret-redemptions"],
    queryFn: () =>
      ClientsService.findAllRedemptions({
        accountId,
        enrollmentId,
        sortField,
        sortOrder,
        pageNumber,
        pageSize,
        mechanism,
      }),
    enabled: !!enrollmentId && !!accountId,
  });

  return {
    redemptionsResponse,
    isLoadingRedemptions,
    isFetchingRedemptions,
    refetchRedemptions,
  };
};
